import { useState, useEffect } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Table, { Column } from '../components/Table';
import LookupSelect, { LookupOption } from '../components/LookupSelect';

const fetchDepartmentOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get(`/masters/departments?search=${encodeURIComponent(search)}`);
  return res.data.departments.map((d: any) => ({ value: d.id, label: `${d.name} (${d.code})` }));
};

export default function Memos() {
  const [memos, setMemos] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ memoTitle: '', memoContent: '', targetDepartmentId: '', targetAll: true });
  const [file, setFile] = useState<File | null>(null);

  const fetchMemos = async () => {
    const res = await api.get('/hr/memos');
    setMemos(res.data.memos || []);
  };

  useEffect(() => {
    fetchMemos();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const data = new FormData();
    data.append('memoTitle', form.memoTitle);
    data.append('memoContent', form.memoContent);
    data.append('targetAll', String(form.targetAll));
    if (!form.targetAll && form.targetDepartmentId) data.append('targetDepartmentId', form.targetDepartmentId);
    if (file) data.append('attachment', file);
    try {
      await api.post('/hr/memos', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm({ memoTitle: '', memoContent: '', targetDepartmentId: '', targetAll: true });
      setFile(null);
      setShowForm(false);
      fetchMemos();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to send memo');
    }
  };

  const remove = async (id: string) => {
    await api.delete(`/hr/memos/${id}`);
    fetchMemos();
  };

  const columns: Column<any>[] = [
    { key: 'memo_title', header: 'Title' },
    { key: 'target_department_name', header: 'Target', render: (r) => (r.target_all ? 'All Employees' : r.target_department_name || '-') },
    { key: 'send_date', header: 'Sent', render: (r) => new Date(r.send_date).toLocaleDateString() },
    { key: 'actions', header: '', render: (r) => (
      <button className="text-red-600 hover:underline text-sm" onClick={() => remove(r.id)}>Delete</button>
    ) },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Memos / Announcements</h1>
        <Button onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : 'New Memo'}</Button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white rounded-lg shadow p-6 space-y-4 mb-6">
          <Input label="Title" value={form.memoTitle} onChange={(e) => setForm({ ...form, memoTitle: e.target.value })} required />
          <textarea className="w-full border rounded-lg px-3 py-2" rows={4} placeholder="Content" value={form.memoContent} onChange={(e) => setForm({ ...form, memoContent: e.target.value })} required />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.targetAll} onChange={(e) => setForm({ ...form, targetAll: e.target.checked })} />
            Send to all employees
          </label>
          {!form.targetAll && (
            <LookupSelect label="Department" value={form.targetDepartmentId} onChange={(v) => setForm({ ...form, targetDepartmentId: v })} fetchOptions={fetchDepartmentOptions} />
          )}
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <Button type="submit">Send Memo</Button>
        </form>
      )}

      <Table columns={columns} rows={memos} emptyMessage="No memos sent" />
    </div>
  );
}
